import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import Tooltip from '@mui/material/Tooltip';
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type DetailedHTMLProps,
  type HTMLAttributes,
  type JSX,
  type Ref,
} from 'react';

import { HELP_LANGUAGE_EVENT, readLanguagePref } from '@/help/preference';

/** The element's own API: it opens and closes its drawer, which it mounts on `document.body`. */
interface GrecoHelpElement extends HTMLElement {
  toggle?: () => void;
}

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'greco-help': DetailedHTMLProps<HTMLAttributes<GrecoHelpElement>, GrecoHelpElement> & {
        'base-url'?: string;
        project?: string;
        'public-key'?: string;
        lang?: string;
        ref?: Ref<GrecoHelpElement>;
      };
    }
  }
}

const HELP_ADMIN_URL = (import.meta.env.VITE_HELP_ADMIN_URL ?? '').replace(/\/+$/, '');
const HELP_PROJECT = import.meta.env.VITE_HELP_PROJECT ?? '';
const HELP_PUBLIC_KEY = import.meta.env.VITE_HELP_PUBLIC_KEY ?? '';
const CONFIGURED = Boolean(HELP_ADMIN_URL && HELP_PROJECT && HELP_PUBLIC_KEY);

const SCRIPT_ATTRIBUTE = 'data-greco-help-script';
const LABEL = 'User Guide';

/** The Web Component registers itself; loading it twice would redefine `greco-help`. */
const loadWidgetScript = (): void => {
  if (document.querySelector(`script[${SCRIPT_ATTRIBUTE}]`)) return;
  const script = document.createElement('script');
  script.type = 'module';
  script.src = `${HELP_ADMIN_URL}/widget/v1/greco-help.js`;
  script.setAttribute(SCRIPT_ATTRIBUTE, 'true');
  document.head.appendChild(script);
};

const subscribeLanguage = (onChange: () => void): (() => void) => {
  window.addEventListener(HELP_LANGUAGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(HELP_LANGUAGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
};

/**
 * Whether one of the app's modal dialogs is on screen. The sidebar marks its own panels with
 * `data-help-panel`, so its drawer never counts as one.
 */
const appDialogOpen = (): boolean =>
  Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]')).some(
    (el) => !el.hasAttribute('data-help-panel') && el.getClientRects().length > 0,
  );

/**
 * Watched rather than measured once: the button has to move above the backdrop the moment a
 * dialog takes the screen over. MUI mounts a dialog into a fresh portal and unmounts it on close.
 */
const useDialogOpen = (): boolean => {
  const [open, setOpen] = useState(false);
  const last = useRef(false);

  useEffect(() => {
    const read = (): void => {
      const next = appDialogOpen();
      // Guarded: the observer hears every grid re-render.
      if (next === last.current) return;
      last.current = next;
      setOpen(next);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return open;
};

/** The top bar's bordered 38 px square, like the bell beside it. */
const square = {
  width: 38,
  height: 38,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
} as const;

/**
 * The user guide: GrECo Help Admin's sidebar (`<greco-help>`), opened from a "?" in the top bar.
 *
 * The element's own "User Guide" launcher is hidden and this button calls its `toggle()`, so the
 * top bar keeps its own look; the drawer, its content and its search all come from Help Admin.
 * The page's screens come from `data-help-screen` on the shell (`help/screens.ts`), the language
 * from the user menu. Renders nothing unless all three `VITE_HELP_*` settings are present.
 *
 * The app bar is its own stacking context, so the "?" cannot be lifted over a dialog's backdrop
 * from inside it: while a dialog is up, a portaled copy stands on the same rectangle instead.
 */
export function HelpAdminGuide(): JSX.Element | null {
  const language = useSyncExternalStore(subscribeLanguage, readLanguagePref);
  const element = useRef<GrecoHelpElement>(null);
  const dock = useRef<HTMLButtonElement>(null);
  const dialogOpen = useDialogOpen();
  const [dockRect, setDockRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (CONFIGURED) loadWidgetScript();
  }, []);

  useEffect(() => {
    // The rect is only read while a dialog is open, so nothing needs resetting when it closes.
    if (!dialogOpen) return;
    const measure = (): void => {
      if (dock.current) setDockRect(dock.current.getBoundingClientRect());
    };
    const frame = window.requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
    };
  }, [dialogOpen]);

  if (!CONFIGURED) return null;

  // Undefined until the script has registered the element; a click before that does nothing.
  const toggle = (): void => element.current?.toggle?.();

  return (
    <>
      <greco-help
        ref={element}
        base-url={HELP_ADMIN_URL}
        project={HELP_PROJECT}
        public-key={HELP_PUBLIC_KEY}
        lang={language}
        style={{ display: 'none' }}
      />
      <Tooltip title={dialogOpen ? '' : LABEL}>
        <IconButton
          ref={dock}
          size="small"
          aria-label={`Open ${LABEL}`}
          data-help="shell-help"
          onClick={toggle}
          // While the elevated copy stands on top of it, this one is inert: behind the backdrop it
          // could not be clicked anyway, and the accessibility tree would announce it twice.
          {...(dialogOpen ? { 'aria-hidden': true, tabIndex: -1 } : {})}
          sx={{ ...square, ...(dialogOpen ? { visibility: 'hidden' } : {}) }}
        >
          <HelpOutlineOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      {dialogOpen && (
        <Portal>
          <Tooltip title={LABEL}>
            <IconButton
              size="small"
              aria-label={`Open ${LABEL}`}
              onClick={toggle}
              sx={{
                ...square,
                position: 'fixed',
                ...(dockRect
                  ? { top: dockRect.top, left: dockRect.left }
                  : // Only if the measurement failed: roughly where the app bar keeps it.
                    { top: 11, right: 32 }),
                zIndex: (t) => t.zIndex.modal + 1,
                color: 'primary.main',
                boxShadow: '0 2px 10px rgba(31, 47, 69, 0.35)',
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <HelpOutlineOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Portal>
      )}
    </>
  );
}
